/**
 * 乐观补齐 canvas snapshot.referenceSources，供未展开历史时 collapsed 草稿边解析。
 *
 * 画布 referenceable：分镜图/视频的 mention.tagId = planId；
 * 资产图：mention.tagId = 标签 id。
 * patchSnapshotDraft 只回写 drafts 时若不补 sources，buildReferenceSourceTargetMap
 * 会缺 sourcePlanId/sourceTagId → D1-draft-no-projection 后无边。
 */
import type { CanvasReferenceSource, CanvasSnapshot } from '@/types/canvas-snapshot';
import type {
  GenerationReferenceDraft,
  GenerationReferenceItem,
} from '@/types/generation-reference';
import { GenerationResourceType } from '@/types/generation-reference';
import type { MentionResource } from '@/types/mention';

export interface ReferenceSourceHint {
  sourcePlanId?: string | null;
  sourceTagId?: string | null;
}

const hintsByIdentity = new Map<string, ReferenceSourceHint>();

function resourceIdentityKey(resourceType: number, resourceId: string): string {
  return `${resourceType}:${resourceId}`;
}

export function clearReferenceSourceHints(): void {
  hintsByIdentity.clear();
}

export function getReferenceSourceHint(
  resourceType: number,
  resourceId: string
): ReferenceSourceHint | undefined {
  return hintsByIdentity.get(resourceIdentityKey(resourceType, resourceId));
}

/**
 * 从 MentionResource 记录 collapsed 映射提示。
 * - STORYBOARD_IMAGE(2) / STORYBOARD_VIDEO(3)：tagId → sourcePlanId
 * - PROJECT_RESOURCE(1)：tagId → sourceTagId
 */
export function rememberReferenceSourceHint(resource: MentionResource): void {
  const resourceId = resource.submitResourceId;
  const resourceType = resource.submitResourceType;
  const tagId = resource.tagId?.trim();
  if (!resourceId || !resourceType || !tagId) return;

  const key = resourceIdentityKey(resourceType, resourceId);
  if (
    resourceType === GenerationResourceType.STORYBOARD_IMAGE ||
    resourceType === GenerationResourceType.STORYBOARD_VIDEO
  ) {
    hintsByIdentity.set(key, { sourcePlanId: tagId, sourceTagId: null });
    return;
  }
  if (resourceType === GenerationResourceType.PROJECT_RESOURCE) {
    hintsByIdentity.set(key, { sourcePlanId: null, sourceTagId: tagId });
  }
}

function buildPlaceholderSource(
  reference: GenerationReferenceItem,
  hint: ReferenceSourceHint
): CanvasReferenceSource {
  return {
    resourceType: reference.resourceType,
    resourceId: reference.resourceId,
    snapshot: reference.snapshot,
    available: true,
    sourcePlanId: hint.sourcePlanId ?? null,
    sourceTagId: hint.sourceTagId ?? null,
    crossStoryboard: false,
  };
}

function mergeHintIntoSource(
  source: CanvasReferenceSource,
  hint: ReferenceSourceHint
): CanvasReferenceSource {
  const nextPlanId = source.sourcePlanId || hint.sourcePlanId || null;
  const nextTagId = source.sourceTagId || hint.sourceTagId || null;
  if (nextPlanId === source.sourcePlanId && nextTagId === source.sourceTagId) {
    return source;
  }
  return {
    ...source,
    sourcePlanId: nextPlanId,
    sourceTagId: nextTagId,
  };
}

/**
 * 用本地 hint 补齐 snapshot.referenceSources（仅补缺，不覆盖服务端已有映射）。
 */
export function enrichSnapshotReferenceSources(
  snapshot: CanvasSnapshot,
  draft: GenerationReferenceDraft
): CanvasSnapshot {
  if (draft.references.length === 0) return snapshot;

  const byIdentity = new Map(
    snapshot.referenceSources.map((source) => [
      resourceIdentityKey(source.resourceType, source.resourceId),
      source,
    ])
  );
  let changed = false;

  for (const reference of draft.references) {
    const key = resourceIdentityKey(reference.resourceType, reference.resourceId);
    const hint = hintsByIdentity.get(key);
    if (!hint?.sourcePlanId && !hint?.sourceTagId) continue;

    const existing = byIdentity.get(key);
    if (!existing) {
      byIdentity.set(key, buildPlaceholderSource(reference, hint));
      changed = true;
      continue;
    }
    const merged = mergeHintIntoSource(existing, hint);
    if (merged !== existing) {
      byIdentity.set(key, merged);
      changed = true;
    }
  }

  if (!changed) return snapshot;
  return {
    ...snapshot,
    referenceSources: Array.from(byIdentity.values()),
  };
}
